// hooks/useVideoRecorder.js — Anamoria SPA
// v1.0 — Video recording hook (May 16, 2026)
//
// Ported from useRecorder.js (voice) with video-specific additions per
// Video Recording Best Practice Review v1.2 locked decisions.
//
// Decisions implemented:
//   #6  (amended): 5-minute recording cap (was 60s; amended to match voice cap)
//   #8:  100 MB file size hard-stop
//   #10: Pause/resume supported (matching voice 4-state UX)
//   #12: 720p target / 1080p ceiling / 30fps capture constraints
//   BPR §1.2:  4-tier MIME type negotiation
//   BPR §1.4:  Camera selection (facingMode toggle, localStorage persistence)
//   BPR §1.5:  Preview mirroring decision exposed (facingMode state)
//   BPR §1.10: Live preview stream exposed for <video srcObject>
//   BPR §1.11: Permission pre-flight via navigator.permissions.query
//   BPR §1.12: Live byte counter + 90% warning + 100% hard-stop
//   BPR §9:    beforeunload warning during recording
//
// Stream cleanup strategy (session decision):
//   - Release all tracks at stopRecording() (camera light OFF on review)
//   - Re-acquire stream on re-record (no re-prompt if permission = 'granted')
//   - Defense-in-depth: cleanup on unmount and beforeunload
//
// Timeslice: 5000ms per session decision (better keyframe alignment,
//   fewer in-memory blobs, adequate byte-counter granularity).
//
// State machine: idle → recording → paused → stopped → (uploading/done
//   handled by VideoPage, not this hook — same pattern as useRecorder.js)
//
// Returns:
//   recordingState    — 'idle' | 'recording' | 'paused' | 'stopped'
//   videoBlob         — Blob | null
//   mimeType          — string
//   duration          — seconds (integer)
//   durationFormatted — 'M:SS'
//   byteCount         — number (bytes accumulated so far)
//   atWarning         — boolean (true when ≥90% of either limit)
//   error             — string | null
//   previewStream     — MediaStream | null (for live <video> preview)
//   facingMode        — 'user' | 'environment'
//   permissionState   — 'prompt' | 'granted' | 'denied' | null
//   startRecording()  — async; acquires stream + starts MediaRecorder
//   pauseRecording()  — pauses MediaRecorder + timer
//   resumeRecording() — resumes MediaRecorder + timer
//   stopRecording()   — stops MediaRecorder + releases stream
//   reRecord()        — resets all state to idle
//   toggleCamera()    — async; flips facingMode, persists to localStorage
//   checkPermission() — async; queries camera permission state

import { useState, useRef, useCallback, useEffect } from 'react';

/* ═══════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════ */

// Duration cap — 5 minutes cumulative recording time (Decision #6 amended).
// Paused time does NOT count. Matches voice recording cap.
const MAX_RECORDING_MS = 300_000;

// File size cap — 100 MB (Decision #8).
const MAX_BYTE_COUNT = 100 * 1024 * 1024;

// Warning threshold — 90% of either limit (BPR §1.12).
const WARNING_RATIO = 0.9;

// Timeslice — 5 seconds (session decision: keyframe alignment + byte counting).
const TIMESLICE_MS = 5000;

// localStorage key for camera facing mode persistence (BPR §1.4).
const FACING_MODE_KEY = 'anamoria_facingMode';

/* ═══════════════════════════════════════
   MIME TYPE NEGOTIATION (BPR §1.2)
   4-tier priority: iOS Safari → Chrome/FF/Edge → older Chrome → generic
   ═══════════════════════════════════════ */

function getSupportedMimeType() {
  const candidates = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  for (const type of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return '';
}

/* ═══════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════ */

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function getStoredFacingMode() {
  try {
    const stored = localStorage.getItem(FACING_MODE_KEY);
    if (stored === 'user' || stored === 'environment') return stored;
  } catch {
    // localStorage may throw in private browsing or when quota is exceeded.
  }
  return 'user'; // Default: front camera
}

function storeFacingMode(mode) {
  try {
    localStorage.setItem(FACING_MODE_KEY, mode);
  } catch {
    // Silently ignore — persistence is a convenience, not critical.
  }
}

/* ═══════════════════════════════════════
   CAPTURE CONSTRAINTS (Decision #12)
   720p target, 1080p ceiling, 30fps.
   facingMode passed dynamically.
   ═══════════════════════════════════════ */

function buildConstraints(facingMode) {
  return {
    video: {
      width: { ideal: 1280, max: 1920 },
      height: { ideal: 720, max: 1080 },
      frameRate: { ideal: 30, max: 30 },
      facingMode,
    },
    audio: true,
  };
}

/* ═══════════════════════════════════════
   HOOK
   ═══════════════════════════════════════ */

export function useVideoRecorder() {
  // ─── State ───
  const [recordingState, setRecordingState] = useState('idle');
  const [videoBlob, setVideoBlob] = useState(null);
  const [mimeType, setMimeType] = useState('');
  const [duration, setDuration] = useState(0);
  const [byteCount, setByteCount] = useState(0);
  const [atWarning, setAtWarning] = useState(false);
  const [error, setError] = useState(null);
  const [previewStream, setPreviewStream] = useState(null);
  const [facingMode, setFacingMode] = useState(getStoredFacingMode);
  const [permissionState, setPermissionState] = useState(null);

  // ─── Refs (mutable, not triggering re-renders) ───
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const byteCountRef = useRef(0);
  const timerRef = useRef(null);
  const lastTickRef = useRef(null);
  const durationMsRef = useRef(0);
  // Ref mirror of recordingState for use in callbacks/effects that
  // close over stale state. Updated synchronously alongside setRecordingState.
  const recordingStateRef = useRef('idle');

  // ─── Helper: update recordingState + ref in sync ───
  function setStateAndRef(newState) {
    recordingStateRef.current = newState;
    setRecordingState(newState);
  }

  // ─── Timer (rAF-based, same pattern as useRecorder.js) ───
  // Tracks cumulative recording time only — paused time doesn't count.

  function startTimer() {
    lastTickRef.current = Date.now();
    const tick = () => {
      const now = Date.now();
      durationMsRef.current += now - lastTickRef.current;
      lastTickRef.current = now;
      const secs = Math.floor(durationMsRef.current / 1000);
      setDuration(secs);
      updateWarning(durationMsRef.current, byteCountRef.current);

      // Hard-stop at 5-minute cap
      if (durationMsRef.current >= MAX_RECORDING_MS) {
        hardStop();
        return;
      }
      timerRef.current = requestAnimationFrame(tick);
    };
    timerRef.current = requestAnimationFrame(tick);
  }

  function stopTimer() {
    if (timerRef.current) {
      cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
    }
  }

  function pauseTimer() {
    stopTimer();
  }

  function resumeTimer() {
    lastTickRef.current = Date.now();
    const tick = () => {
      const now = Date.now();
      durationMsRef.current += now - lastTickRef.current;
      lastTickRef.current = now;
      setDuration(Math.floor(durationMsRef.current / 1000));
      updateWarning(durationMsRef.current, byteCountRef.current);

      if (durationMsRef.current >= MAX_RECORDING_MS) {
        hardStop();
        return;
      }
      timerRef.current = requestAnimationFrame(tick);
    };
    timerRef.current = requestAnimationFrame(tick);
  }

  // ─── Warning check (BPR §1.12) ───
  // 90% of either limit triggers the warning.

  function updateWarning(ms, bytes) {
    const timeWarning = ms >= MAX_RECORDING_MS * WARNING_RATIO;
    const sizeWarning = bytes >= MAX_BYTE_COUNT * WARNING_RATIO;
    setAtWarning(timeWarning || sizeWarning);
  }

  // ─── Hard-stop (invoked by timer or byte-count exceeding cap) ───

  function hardStop() {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      stopTimer();
      mediaRecorderRef.current.stop();
      // State transition to 'stopped' happens in the onstop handler.
    }
  }

  // ─── Stream release (session decision: release at stop) ───
  // Stops all tracks → camera light OFF → stream nulled.

  function releaseStream() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setPreviewStream(null);
  }

  // ─── Cleanup on unmount (defense-in-depth) ───

  useEffect(() => {
    return () => {
      stopTimer();
      releaseStream();
    };
  }, []);

  // ─── beforeunload warning (BPR §9) ───
  // Active when recording or paused. Removed on stop/idle/unmount.

  useEffect(() => {
    function handleBeforeUnload(e) {
      if (
        recordingStateRef.current === 'recording' ||
        recordingStateRef.current === 'paused'
      ) {
        e.preventDefault();
        // Modern browsers ignore custom messages but require returnValue.
        e.returnValue = '';
      }
    }

    if (recordingState === 'recording' || recordingState === 'paused') {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [recordingState]);

  // ─── Permission pre-flight (BPR §1.11) ───

  const checkPermission = useCallback(async () => {
    try {
      // navigator.permissions.query({ name: 'camera' }) is supported in
      // Chrome, Edge, and Firefox. Safari does not support it — the catch
      // block handles that gracefully by setting state to null (unknown).
      const result = await navigator.permissions.query({ name: 'camera' });
      setPermissionState(result.state); // 'prompt' | 'granted' | 'denied'
      // Listen for changes (e.g., user revokes permission in browser settings).
      result.onchange = () => {
        setPermissionState(result.state);
      };
    } catch {
      // Safari doesn't support permissions.query for camera.
      // Set null = unknown; VideoPage will handle this by attempting
      // getUserMedia directly and catching NotAllowedError.
      setPermissionState(null);
    }
  }, []);

  // ─── Start recording ───

  const startRecording = useCallback(async () => {
    setError(null);
    setVideoBlob(null);
    setDuration(0);
    setByteCount(0);
    setAtWarning(false);
    durationMsRef.current = 0;
    byteCountRef.current = 0;
    chunksRef.current = [];

    // Feature detection
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Video recording is not supported in this browser.');
      return;
    }

    const mime = getSupportedMimeType();

    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        buildConstraints(facingMode)
      );
      streamRef.current = stream;
      setPreviewStream(stream);
      setMimeType(mime);

      const recorder = new MediaRecorder(
        stream,
        mime ? { mimeType: mime } : {}
      );
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
          byteCountRef.current += e.data.size;
          setByteCount(byteCountRef.current);
          updateWarning(durationMsRef.current, byteCountRef.current);

          // Hard-stop on file size cap
          if (byteCountRef.current >= MAX_BYTE_COUNT) {
            hardStop();
          }
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mime || 'video/webm',
        });
        setVideoBlob(blob);
        // Release camera/mic — camera light OFF on review screen.
        // Re-record will re-acquire the stream (session decision).
        releaseStream();
        setStateAndRef('stopped');
      };

      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setError('Recording failed. Please try again.');
        releaseStream();
        setStateAndRef('idle');
      };

      recorder.start(TIMESLICE_MS);
      setStateAndRef('recording');
      startTimer();
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError(
          'Camera and microphone access denied. Please allow access in your browser settings and try again.'
        );
        setPermissionState('denied');
      } else if (err.name === 'NotFoundError') {
        setError(
          'No camera found. Please connect a camera and try again.'
        );
      } else if (err.name === 'OverconstrainedError') {
        // Constraints too strict for this device — retry with relaxed constraints.
        // This shouldn't happen with { ideal } values, but defensive handling.
        setError(
          'Your camera does not support the required video quality. Please try a different device.'
        );
      } else {
        setError('Failed to start recording. Please try again.');
      }
      console.error('startRecording error:', err);
    }
  }, [facingMode]);

  // ─── Pause (Decision #10) ───

  const pauseRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      recordingStateRef.current === 'recording'
    ) {
      mediaRecorderRef.current.pause();
      pauseTimer();
      setStateAndRef('paused');
    }
  }, []);

  // ─── Resume (Decision #10) ───

  const resumeRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      recordingStateRef.current === 'paused'
    ) {
      mediaRecorderRef.current.resume();
      resumeTimer();
      setStateAndRef('recording');
    }
  }, []);

  // ─── Stop ───

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      (recordingStateRef.current === 'recording' ||
        recordingStateRef.current === 'paused')
    ) {
      stopTimer();
      mediaRecorderRef.current.stop();
      // State transition + stream release happen in the onstop handler.
    }
  }, []);

  // ─── Re-record ───
  // Resets all state to idle. Stream was already released in onstop.
  // Next startRecording() will re-acquire the stream (session decision).

  const reRecord = useCallback(() => {
    stopTimer();
    releaseStream();
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    byteCountRef.current = 0;
    durationMsRef.current = 0;
    setVideoBlob(null);
    setDuration(0);
    setByteCount(0);
    setAtWarning(false);
    setError(null);
    setStateAndRef('idle');
  }, []);

  // ─── Toggle camera (BPR §1.4) ───
  // Flips facingMode and persists. If currently recording, this is a no-op
  // (camera toggle during recording is not supported in v1 — the MediaRecorder
  // is bound to the stream it started with; switching requires stop + restart).
  // VideoPage should disable the toggle button during recording/paused states.

  const toggleCamera = useCallback(async () => {
    if (
      recordingStateRef.current === 'recording' ||
      recordingStateRef.current === 'paused'
    ) {
      // Cannot toggle camera mid-recording — MediaRecorder is bound to stream.
      return;
    }
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    storeFacingMode(newMode);
  }, [facingMode]);

  // ─── Return shape ───

  return {
    recordingState,
    videoBlob,
    mimeType,
    duration,
    durationFormatted: formatDuration(duration),
    byteCount,
    atWarning,
    error,
    previewStream,
    facingMode,
    permissionState,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    reRecord,
    toggleCamera,
    checkPermission,
  };
}
